// frontend/src/components/WhatWeCheck.js
import { useState } from "react";

const CHECKS = [
  {
    group: "TLS / HTTPS",
    status: "live",
    items: [
      "Certificate validity and trust chain",
      "Certificate expiry and hostname match",
      "Negotiated protocol version (TLS 1.0–1.3)",
      "Cipher suite strength",
      "HSTS presence and configuration quality",
    ],
  },
  {
    group: "Security Headers",
    status: "live",
    items: [
      "Content-Security-Policy (presence + quality: unsafe-inline, unsafe-eval, wildcards)",
      "X-Frame-Options (clickjacking)",
      "X-Content-Type-Options",
      "Referrer-Policy",
      "Permissions-Policy",
      "Server / X-Powered-By version disclosure",
      "Advertised HTTP methods (TRACE / PUT / DELETE)",
    ],
  },
  {
    group: "Email Security",
    status: "live",
    items: [
      "SPF record presence and strength",
      "DKIM on common selectors",
      "DMARC policy (p=none vs quarantine/reject)",
      "MX records present",
      "Composite spoofability — can someone impersonate your email?",
    ],
  },
  {
    group: "DNS & Domain",
    status: "live",
    items: [
      "A records",
      "CAA (who may issue certificates for you)",
      "DNSSEC (DS at the parent zone)",
    ],
  },
  {
    group: "Domain Health",
    status: "live",
    items: [
      "Registration expiry date (via RDAP)",
      "Days remaining and renewal risk",
    ],
  },
  {
    group: "CORS Policy",
    status: "live",
    items: [
      "Reflected-origin misconfiguration",
      "Wildcard + credentials combination",
      "Permissive read-only policy",
    ],
  },
  {
    group: "Exposed Files",
    status: "live",
    items: [
      ".git/ source control metadata",
      ".env secrets files",
      "Database backups (.sql)",
      "phpinfo(), SVN metadata",
      "Config backups (wp-config.php.bak, config.php.bak)",
      ".htaccess, .DS_Store",
      "Apache server-status, JS source maps",
      "swagger.json, /admin/",
      "Directory listing detection",
      "security.txt presence",
    ],
  },
  {
    group: "Cookie Security",
    status: "live",
    items: ["Secure flag", "HttpOnly flag", "SameSite attribute"],
  },
  {
    group: "Content & Client",
    status: "live",
    items: [
      "Mixed content (HTTP assets on HTTPS pages)",
      "Privacy policy link on homepage",
      "Cookie consent signal detected",
    ],
  },
  {
    group: "Infrastructure",
    status: "live",
    items: [
      "WAF / CDN detection",
      "HTTP → HTTPS redirect",
      "Hosting geo-location (POPIA data residency)",
      "Domain age via RDAP",
    ],
  },
  {
    group: "POPIA Readiness",
    status: "live",
    items: [
      "Privacy policy linked from homepage",
      "Cookie consent signal detected",
      "Contact forms served over HTTPS",
    ],
  },
  {
    group: "Coming soon",
    status: "planned",
    items: [
      "Have I Been Pwned — prior breach history",
      "Google Safe Browsing — malware/phishing flag",
      "Subdomain enumeration (Certificate Transparency)",
      "Outdated JS library detection (jQuery, etc.)",
      "WHOIS registrar and abuse contacts",
      "IP reputation and spam blacklist checks",
    ],
  },
];

const NOT_DONE = [
  "No logins, passwords, or credentials are ever tested.",
  "No exploitation attempts of any kind.",
  "No port scanning beyond a single TLS handshake on 443.",
  "No SQL injection, XSS, or other active vulnerability probing.",
];

export default function WhatWeCheck() {
  const [open, setOpen] = useState(false);

  return (
    <section className="mt-14 rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span>
          <span className="block text-sm font-semibold text-gray-900">
            What CyberCheck checks
          </span>
          <span className="mt-0.5 block text-xs text-gray-500">
            {CHECKS.reduce((n, g) => n + g.items.length, 0)} checks across{" "}
            {CHECKS.length} categories — all passive and non-invasive.
          </span>
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {CHECKS.map((g) => (
              <div key={g.group}>
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                    {g.group}
                  </h4>
                  {g.status === "planned" && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                      Planned
                    </span>
                  )}
                </div>
                <ul className="mt-1.5 space-y-1">
                  {g.items.map((item) => (
                    <li key={item} className="flex gap-2 text-xs text-gray-600">
                      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-rose-800">
              What we deliberately do not do
            </h4>
            <ul className="mt-1.5 space-y-1">
              {NOT_DONE.map((item) => (
                <li key={item} className="flex gap-2 text-xs text-rose-900">
                  <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-rose-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}